import {
   Box,
   Grid,
   Switch,
   Typography,
   useMediaQuery,
   useTheme
} from "@mui/material";
import Button from "@mui/material/Button";
import Vimeo, { EndEvent, PauseEvent, PlayEvent } from "@u-wave/react-vimeo";
import Player from "@vimeo/player";
import parse from "html-react-parser";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router";
import { Scrollbar } from "react-scrollbars-custom";
import { handlePostAxiosRequest } from "../../api/addflag";
import { dummyPgPromo } from "../../dummy/pgPromo";
import { axiosRequest, axiosRequestData } from "../../helpers/axios";
import { calculateWatchedLessons, removeDomainFromUrl, toggleAutoplay } from "../../helpers/utils";
import { useCoachingBannerVisibility } from "../../hooks/useCoachingBannerVisibility";
import { useUser } from "../../hooks/userDataProvider";
import { useVideoProgressTracking } from '../../hooks/useVideoProgressTracking';
import { Accelerator } from "../../interfaces/accelerator";
import { Course, CourseType } from "../../interfaces/course";
import { CurrentLesson } from "../../interfaces/currentLesson";
import { Lesson } from "../../interfaces/lesson";
import { Promo } from "../../interfaces/promo";
import CoursePlaylist from "../coursePlaylist/coursePlaylist";
import NextLesson from "../dialogs/nextLesson/nextLesson";
import NextLessonModal from "../dialogs/nextLessonModal/nextLessonModal";
import PopUpDialog from "../dialogs/popUpDialog/popUpDialog";
import PopUpDialogMobile from "../dialogs/popUpDialog/popUpDialogMobile";
import UpNext from "../dialogs/upNextPlaylist/upNext";
import MotivationMessages from "../headers/motivationMessages/motivationMessages";
import { AxiosResponse } from "axios";
import { ApiCoursesResponse } from "../../interfaces/course";
import {
   clickVipProductRecommendation,
   clickedVipButtonGolfSchool,
   clickedVipGolfSchool,
   lessonVideoCompleteEvent,
   pausedVideoEvent,
   startedVideoEvent,
   trainingCourseCompleted,
   viewChampionPassModal,
   viewProduct,
   viewedVipGolfSchool,
   videoAutoplayToggleEvent,
   replayLessonEvent,
   totalTimeWatched
} from "../mixPanelEvents/mixPanelEvents";
import { BorderLinearProgressHeight6 } from "../playlist/accoridonStyles";
import styles from "./coursePlayer.module.css";
import GuidedJourneyModal from "../dialogs/guidedJourneyModal/guidedJourneyModal";
import TakeSwingSurveyDialog from "../dialogs/takeSwingSurveyDialog/takeSwingSurveyDialog";
import LessonSurvey from "../dialogs/lessonSurvey/lessonSurvey";
import AutoplayPopover from "../dialogs/autoplayPopover";
import Spinner from "../spinner";
import Tooltip from "@mui/material/Tooltip";
import TrainingCardCarousel from "../../components/cards/trainingCards/trainingCardCarousel";
import { Thing } from "../../interfaces/course";
export const CHAMPION_PASS_PROMO = {
   ID: 124,
   image: "https://members.performancegolf.com/wp-content/uploads/2024/07/pg-coaches-2.png",
   name: "Champions Pass",
   promo_text: "Start Your Free 7 Day Trial and Access All of Our Best Selling Courses from the World's Top Coaches for Over 90% Off!",
   course_desc: "Start Your Free 7 Day Trial and Access All of Our Best Selling Courses from the World's Top Coaches for Over 90% Off!",
   url: "https://email.performancegolfzone.net/15HcaC",
   accelerator: 1
};

const MOVE_TO_NEXT_LESSON: string = window.pg?.l10n.next_lesson || "Move to Next Lesson?";
const MOVE_TO_VIP: string = "Want to Learn More?";
const MOVE_TO_NEXT_LESSON_MOBILE: string = "Move to Next Lesson?";
declare global {
   interface Window {
      pgPromo: any; // Adjust the type as needed
      setShowPopupDialog: (value: boolean) => any;
      pgUser: any;
      is_autoplay: string | undefined;
      pg: any;
   }
}

declare global {
   interface Window {
      pgPromo: any; // Adjust the type as needed
      setShowPopupDialog: (value: boolean) => any;
      pgUser: any;
   }
}

const AutoplayIcon = ({
   onClick,
   isMobile,
}: {
   onClick: () => void;
   isMobile: boolean;
}) => (
   <Box
      onClick={onClick}
      sx={{
         display: "inline-flex",
         alignItems: "center",
         justifyContent: "center",
         width: isMobile ? "18px" : "22px",
         height: isMobile ? "18px" : "22px",
         borderRadius: "50%",
         border: "1.5px solid #7B726C",
         color: "#7B726C",
         fontSize: isMobile ? "13px" : "15px",
         cursor: "pointer",
         userSelect: "none",
         marginRight: "6px",
         lineHeight: 1,
         transition: "background 0.15s ease",

         "&:hover": {
            background: "rgba(0,0,0,0.05)",
         },
      }}
   >
      {"<"}
   </Box>
);

const CoursePlayer = ({
   accelerator,
   currentLesson,
   showGuidedJourney,
   popularCoursesOnly
}: {
   accelerator: Accelerator;
   currentLesson: CurrentLesson;
   showGuidedJourney: boolean;
   popularCoursesOnly: {
      name: string;
      things: Thing[];
   };
}) => {
   const navigate = useNavigate();
   const [locationForMixpanel, setLocationForMixpanle] = useState("Set Course Focus");
   const [nextCourseLesson, setNextCourseLesson] = useState<any>({ name: "", url: "" });
   const [videoLoaded, setVideoLoaded] = useState(false);
   const [lessonComplete, setLessonComplete] = useState<{
      lessonID: number;
      courseID: number;
      wasComplete: boolean;
      isIntroduction: boolean;
   }>({ lessonID: 0, courseID: 0, wasComplete: false, isIntroduction: false });
   const [showModal, setShowModal] = useState(false);
   const containerRef = useRef<null | HTMLDivElement>(null);
   const [modalTitle, setModalTitle] = useState("");
   const [modalBtnText, setModalBtnText] = useState("");
   const [modalAction, setModalAction] = useState(() => () => { });
   const [courses, setCourses] = useState<Course[]>([]);
   const theme = useTheme();
   const isMobile = useMediaQuery(theme.breakpoints.down("md"));
   const [hiding, setHiding] = useState(false);
   const [expanded, setExpanded] = useState<number | boolean>(false);
   const [playerHeight, setPlayerHeight] = useState(0);
   const [playerTitleHeight, setPlayerTitleHeight] = useState(0);
   const [ctaButtonHeight, setCtaButtonHeight] = useState(0);
   const playerTitleRef = useRef<null | HTMLDivElement>(null);
   const courseHeaderRef = useRef<null | HTMLDivElement>(null);
   const ctaRef = useRef<HTMLButtonElement>(null);
   const contentRef = useRef<null | HTMLDivElement>(null);
   const prevNextRef = useRef<null | HTMLDivElement>(null);
   const [courseTitleHeight, setCourseTitleHeight] = useState(0);
   const [contentHeight, setContentHeight] = useState(0);
   const [videoLessonCount, setVideoLessonCount] = useState(0);
   const [completeLessonsID, setCompleteLessonsID] = useState<number[]>([]);
   const [acceleratorProgress, setAcceleratorProgress] = useState(0);
   const [showPopupDialog, setShowPopupDialog] = useState(false);
   const [completedCourse, setCompletedCourse] = useState(false);
   const [addToPlaylist, setAddToPlaylist] = useState(false);
   const upNextPlaylistRef = useRef<HTMLDivElement>(null);
   let scrollbarsRef = useRef(null);
   const { isShowCoachBanner, updateLessonCounter } = useCoachingBannerVisibility();
   const [isShowBanner, setIsShowBanner] = useState(false)
   const isFirstRender = useRef(true);
   const { isUserSawBanner, setIsUserSawBanner } = useUser();
   const spanVipRef = useRef(null);
   const [isShowChampionsPassModal, setIsShowChampionsPassModal] = useState(false)
   const handleIsShowChampionsPassModal = (value: boolean) => setIsShowChampionsPassModal(value);
   const vimeoPlayerRef = useRef<any>(null);
   const [totalVideoPlayerHeight, setTotalVideoPlayerHeight] = useState<number>(0);
   const [tmpHeaderHeight, setTmpHeaderHeight] = useState<number>(0);
   const [tmpCtaLessonBannerHeight, setTmpCtaLessonBannerHeight] = useState<number>(0);
   const [tmpCtaCurrentLessonBannerHeight, setTmpCtaCurrentLessonBannerHeight] = useState<number>(0);
   const [tmpCtaTypeLessonBannerHeight, setTmpCtaTypeLessonBannerHeight] = useState<number>(0);
   const [preventAutoScroll, setPreventAutoScroll] = useState<boolean>(false);

   const smallScreenRange = useMediaQuery('(min-width:900px) and (max-width:1200px)');
   const tabletsRange = useMediaQuery('(min-width: 769px) and (max-width: 899px)');
   const onlyMobileRange = useMediaQuery('(max-width: 768px)');
   const isMobile767 = useMediaQuery('(max-width:767px)');

   const [showSwingSurveydDialog, setShowSwingSurveydDialog] = useState(false);
   const [selected, setSelected] = useState(0);
   const [showLessonSurveyBanner, setShowLessonSurveyBanner] = useState(false);
   const [showLessonSurvey, setShowLessonSurvey] = useState(false);

   const [showOrNotNextLessonModal, setShowOrNotNextLessonModal] = useState(false);
   const [autoplayNextForCourse, setAutoplayNextForCourse] = useState(accelerator.is_autoplay);
   const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
   const switchAnchorRef = useRef<HTMLElement | null>(null);
   const switchAnchorMobileRef = useRef<HTMLElement | null>(null);
   const switchAnchorDesktopRef = useRef<HTMLElement | null>(null);

   const [videoLessons, setVideoLessons] = useState<Lesson[]>([]);
   const [nextVideoLesson, setNextVideoLesson] = useState<Lesson | null>(null);
   const [prevVideoLesson, setPrevVideoLesson] = useState<Lesson | null>(null);
   const [carouselHeight, setCarouselHeight] = useState(0);
   const [prevNextHeight, setPrevNextHeight] = useState(0);
   const lessonSurveyRef = useRef<null | HTMLDivElement>(null);
   const [lessonSurveyHeight, setLessonSurveyHeight] = useState(0);

   const [previouslyWatchedLesson, setPreviouslyWatchedLesson] = useState<CurrentLesson>(currentLesson);

   const trainingCarouselRef = useRef<HTMLDivElement | null>(null);

   const [containerWidth, setContainerWidth] = useState(0);
   const updateContainerWidth = () => {
      if (containerRef.current) {
         setContainerWidth(containerRef.current.offsetWidth);
      }
   };

   const activeAnchor = isMobile
   ? switchAnchorMobileRef.current
   : switchAnchorDesktopRef.current;


   const [showGifOverlay, setShowGifOverlay] = useState(false);

   const handleCloseTip = () => {
      setAnchorEl(null);
   };

   const open = Boolean(anchorEl);

   window.setShowPopupDialog = function (value: boolean) {
      setShowPopupDialog(true);
   };

   useEffect(() => {
      const observer = new ResizeObserver((entries) => {
         for (let entry of entries) {
            updateContainerWidth(); // Call the function to update container width
         }
      });

      if (containerRef.current) {
         observer.observe(containerRef.current);
         updateContainerWidth(); // Call the function to update container width initially
      }

      return () => {
         observer.disconnect();
      };
   }, [containerRef.current]);


   useEffect(() => {
      if (window.is_autoplay && window.is_autoplay.indexOf("" + accelerator.ID) !== -1) {
         setAutoplayNextForCourse(window.is_autoplay.indexOf("true") !== -1);
      } else {
         setAutoplayNextForCourse(accelerator.is_autoplay);
      }
   }, [accelerator])

   useEffect(() => {
      window.is_autoplay = "" + accelerator.ID + autoplayNextForCourse;
   }, [autoplayNextForCourse])


   const { onProgressUpdate, userStartedAllLessons } = useVideoProgressTracking(currentLesson, accelerator);
   // const checkIfUserStartedAllLessons = async () => {
   //    await userStartedAllLessons();
   // };

   const isIOS = () => {
      const ua = navigator.userAgent;
      // legacy iOS (iPhone/iPad/iPod)
      if (/iPad|iPhone|iPod/.test(ua) && !(window as any).MSStream) {
         return true;
      }
      // iPadOS 13+ Safari detection
      if (/\bMacintosh\b/.test(ua) && navigator.maxTouchPoints > 1) {
         return true;
      }
      return false;
   };

   useEffect(() => {
      if (currentLesson.name === "") {
         return;
      }
      const divEle = document.createElement("div");
      const divImg = document.createElement("img");
      const divParagraph = document.createElement("p");

      divImg.src = "https://www.performancegolf.com/wp-content/uploads/2023/11/Video-Icon.png"
      divImg.style.height = "100%";
      divImg.style.marginRight = "7px";
      const divTextContent = currentLesson.name + " (" + currentLesson.duration + ")";

      divEle.appendChild(divImg);
      divParagraph.textContent = divTextContent;
      divParagraph.style.marginBottom = "0";
      divParagraph.style.fontSize = "14px";
      divParagraph.style.color = "#FCFAFA";

      divEle.appendChild(divParagraph);
      divEle.style.padding = "8px";
      divEle.style.marginLeft = "20px";
      divEle.style.marginRight = "20px";
      divEle.style.background = "#000";
      divEle.style.display = "flex";
      divEle.style.opacity = "0";

      document.body.appendChild(divEle);
      setTimeout(() => {
         setTmpHeaderHeight(divEle.offsetHeight);
      }, 2000);
      setTimeout(() => {
         divEle.remove();
      }, 3000);
   }, [currentLesson.name]);

   useEffect(() => {
      if (!accelerator.showVipBannerToAllLessons) {
         return;
      }

      const buttonEle = document.createElement("button");
      buttonEle.style.background = "#3e59d9";
      buttonEle.style.paddingTop = "18px";
      buttonEle.style.paddingBottom = "18px";
      buttonEle.style.display = "inline-flex";

      const firstSpanEle = document.createElement("span");
      firstSpanEle.innerHTML = accelerator.ctaTextForAllLessons;
      firstSpanEle.style.fontSize = "15px";
      firstSpanEle.style.lineHeight = "18px";
      firstSpanEle.style.fontWeight = "400";

      const secondSpanEle = document.createElement("span");
      secondSpanEle.textContent = "Click Here";
      secondSpanEle.style.fontSize = "15px";
      secondSpanEle.style.lineHeight = "18px";
      secondSpanEle.style.fontWeight = "400";
      secondSpanEle.style.textDecoration = "underline";
      secondSpanEle.style.marginLeft = "8px";

      firstSpanEle.style.color = "white";
      secondSpanEle.style.color = "white";

      buttonEle.style.width = "calc(100% - 40px)";
      buttonEle.style.marginLeft = "20px";
      buttonEle.style.marginRight = "20px";
      buttonEle.style.justifyContent = "center";

      buttonEle.appendChild(firstSpanEle);
      buttonEle.appendChild(secondSpanEle);
      document.body.appendChild(buttonEle);

      setTimeout(() => {
         setTmpCtaLessonBannerHeight(buttonEle.offsetHeight);
      }, 1000);
      setTimeout(() => {
         buttonEle.remove();
      }, 3000);

   }, [accelerator.showVipBannerToAllLessons, accelerator.ctaTextForAllLessons]);

   useEffect(() => {
      if (currentLesson.cta && currentLesson.cta.text) {
         const buttonEle = document.createElement("button");
         buttonEle.style.background = "#3e59d9";
         buttonEle.style.paddingTop = "18px";
         buttonEle.style.paddingBottom = "18px";
         buttonEle.style.display = "inline-flex";

         const firstSpanEle = document.createElement("span");
         firstSpanEle.innerHTML = currentLesson.cta.text;
         firstSpanEle.style.fontSize = "15px";
         firstSpanEle.style.lineHeight = "18px";
         firstSpanEle.style.fontWeight = "400";

         const secondSpanEle = document.createElement("span");
         secondSpanEle.textContent = "Click Here";
         secondSpanEle.style.fontSize = "15px";
         secondSpanEle.style.lineHeight = "18px";
         secondSpanEle.style.fontWeight = "400";
         secondSpanEle.style.textDecoration = "underline";
         secondSpanEle.style.marginLeft = "8px";

         firstSpanEle.style.color = "white";
         secondSpanEle.style.color = "white";

         buttonEle.style.width = "calc(100% - 40px)";
         buttonEle.style.marginLeft = "20px";
         buttonEle.style.marginRight = "20px";
         buttonEle.style.justifyContent = "center";

         buttonEle.appendChild(firstSpanEle);
         buttonEle.appendChild(secondSpanEle);
         document.body.appendChild(buttonEle);

         setTimeout(() => {
            setTmpCtaCurrentLessonBannerHeight(buttonEle.offsetHeight);
         }, 1000);
         setTimeout(() => {
            buttonEle.remove();
         }, 3000);
      }
   }, [currentLesson.cta]);

   useEffect(() => {
      if (currentLesson.cta && currentLesson.cta.text) {
         const buttonEle = document.createElement("button");
         buttonEle.style.background = "#3e59d9";
         buttonEle.style.paddingTop = "18px";
         buttonEle.style.paddingBottom = "18px";
         buttonEle.style.display = "inline-flex";

         const firstSpanEle = document.createElement("span");
         firstSpanEle.innerHTML = `Learn More About a <span class="fw-bold">VIP Experience</span>`;
         firstSpanEle.style.fontSize = "15px";
         firstSpanEle.style.lineHeight = "18px";
         firstSpanEle.style.fontWeight = "400";

         const secondSpanEle = document.createElement("span");
         secondSpanEle.textContent = "Click Here";
         secondSpanEle.style.fontSize = "15px";
         secondSpanEle.style.lineHeight = "18px";
         secondSpanEle.style.fontWeight = "400";
         secondSpanEle.style.textDecoration = "underline";
         secondSpanEle.style.marginLeft = "8px";

         firstSpanEle.style.color = "white";
         secondSpanEle.style.color = "white";

         buttonEle.style.width = "calc(100% - 40px)";
         buttonEle.style.marginLeft = "20px";
         buttonEle.style.marginRight = "20px";
         buttonEle.style.justifyContent = "center";

         buttonEle.appendChild(firstSpanEle);
         buttonEle.appendChild(secondSpanEle);
         document.body.appendChild(buttonEle);

         setTimeout(() => {
            setTmpCtaTypeLessonBannerHeight(buttonEle.offsetHeight);
         }, 1000);
         setTimeout(() => {
            buttonEle.remove();
         }, 3000);
      }
   }, [currentLesson.cta]);

   useEffect(() => {
      setPreviouslyWatchedLesson(currentLesson);
      if (preventAutoScroll && isMobile) {
         return;
      }
      function getVimeoPlayerSize(): [number, number] {
         const width: number = window.innerWidth - 40;
         const height: number = width * 9 / 16;

         return [width, height];
      }

      const [_, height] = getVimeoPlayerSize();
      let bannerHeight = courseHeaderRef.current?.clientHeight ?? 109;
      setTotalVideoPlayerHeight(height + tmpHeaderHeight + bannerHeight);

      const playingLessonId = currentLesson.ID;

      if (isMobile) {
         setExpanded(currentLesson.courseID);
         if (currentLesson.lesson_type === "cta") {
            const playingLessonElements = document.querySelectorAll(`#lesson-id-${playingLessonId}`);
            setTimeout(() => {
               playingLessonElements.forEach(element => {
                  let classNameThing = element.className;
                  if (classNameThing.includes("active")) {
                     const elementTop = element.getBoundingClientRect().top + window.scrollY;
                     const offset = bannerHeight + height + tmpCtaTypeLessonBannerHeight;
                     sessionStorage.setItem("lastHeight", String(0));

                     window.scrollTo({
                        top: elementTop - offset,
                        behavior: "smooth",
                     });
                  }
               })
            }, 1000);
         } else {
            setTimeout(() => {
               const playingLessonElement = document.querySelector(`#lesson-id-${playingLessonId}`);

               if (playingLessonElement) {
                  const elementTop = playingLessonElement.getBoundingClientRect().top + window.scrollY;
                  const offset = Math.round(height + tmpHeaderHeight + bannerHeight + (accelerator.showVipBannerToAllLessons ? tmpCtaLessonBannerHeight : 0) + (currentLesson.cta ? tmpCtaCurrentLessonBannerHeight : 0)) || 0;
                  sessionStorage.setItem("lastHeight", tmpHeaderHeight.toString());

                  window.scrollTo({
                     top: elementTop - offset,
                     behavior: "smooth",
                  });
               }
            }, 1000);
         }
      }
   }, [tmpHeaderHeight, courseHeaderRef, tmpCtaLessonBannerHeight, tmpCtaCurrentLessonBannerHeight]);

   useEffect(() => {
      console.log("test test");
   }, []);

   useEffect(() => {
      if (isMobile) {
         window.addEventListener("resize", (event) => {
            if (window.innerHeight > window.innerWidth) {
               console.log("Orientation changed: Portrait");
               changeViewMod(true);
            } else {
               console.log("Orientation changed: Landscape");
               changeViewMod(false);
            }
         });

         return () => {
            window.removeEventListener("resize", () => { });
         };
      }
   }, []);

   useEffect(() => {
      if (acceleratorProgress === 100) {
         trainingCourseCompleted(currentLesson, accelerator);
      }
   }, [acceleratorProgress]);

   const changeViewMod = (currentViewport: boolean) => {
      if (!vimeoPlayerRef.current) {
         console.warn("Vimeo player ref is not ready");
         return;
      }

      const playerElement = vimeoPlayerRef.current.player?.element;
      if (playerElement) {
         playerElement.setAttribute('allow', 'fullscreen');
         const player = new Player(playerElement);
         if (!currentViewport) {
            if (isIOS()) {
               player.requestFullscreen().then(function () {
                  console.log('enter the fullscreen');
               }).catch(function (e) {
                  console.error(e);
               });
            } else {
               playerElement.classList.add(styles.enterTheFullScreen);
            }
         } else {
            if (isIOS()) {
               player.exitFullscreen().then(function () {
                  console.log('exit the fullscreen');
               }).catch(function (e) {
                  console.error(e);
               });
            } else {
               playerElement.classList.remove(styles.enterTheFullScreen);
            }
         }
      }
   };

   const handleReplay = () => {
      if (!vimeoPlayerRef.current) {
         console.warn("Vimeo player ref is not ready");
         return;
      }
      const iframeEl = vimeoPlayerRef.current.player?.element;
      if (!iframeEl) {
         console.warn("Vimeo iframe element not found");
         return;
      }
      const player = new Player(iframeEl);
      player.setCurrentTime(0);
      player.play();
      replayLessonEvent(currentLesson, accelerator);
      setShowOrNotNextLessonModal(false);
   };
   const handleAutoplayToggle = (event: React.ChangeEvent<HTMLInputElement>) => {
      setAutoplayNextForCourse(event.target.checked);
      if (!showOrNotNextLessonModal) {
         toggleAutoplay(event.target.checked, accelerator.ID);
      }
      let status = event.target.checked ? 'On' : 'Off';

      videoAutoplayToggleEvent(status, currentLesson, accelerator, 'Playlist');
   };

   const didMountRef = useRef(false);
   useEffect(() => {
      if (!didMountRef.current) {
         didMountRef.current = true;
         return;
      }

      if (!autoplayNextForCourse) {
         setAnchorEl(activeAnchor);
      } else {
         setAnchorEl(null);
      }
   }, [autoplayNextForCourse, activeAnchor]);

   const checkCurretViewMode = () => {
      let currentMod = window.innerWidth > window.innerHeight;
      if (currentMod && isMobile) {
         changeViewMod(false);
      }
   };

   useEffect(() => {
      const handleSpanDisplay = () => {
         if (spanVipRef.current) {
            viewedVipGolfSchool("Training with Bumper");
         }
      };
      const observer = new IntersectionObserver(([entry]) => {
         if (entry.isIntersecting) {
            handleSpanDisplay();
            observer.disconnect();
         }
      });
      if (spanVipRef.current) {
         observer.observe(spanVipRef.current);
      }
      return () => {
         observer.disconnect();
      };
   }, [spanVipRef.current]);

   useEffect(() => {
      setCompleteLessonsID(
         accelerator.courses
            .reduce((acc: Lesson[], next: Course) => {
               return [
                  ...acc,
                  ...next.lessons.filter(
                     (lesson) =>
                        !lesson.is_introduction &&
                        lesson.lesson_type === "video" &&
                        lesson.complete
                  ),
               ];
            }, [])
            .map((el) => el.ID)
      );

      setAcceleratorProgress(accelerator.progress);

      const handleResize = () => {
         containerRef.current &&
            setPlayerHeight(containerRef.current.offsetHeight);
         playerTitleRef.current &&
            setPlayerTitleHeight(playerTitleRef.current.offsetHeight);
         courseHeaderRef.current &&
            setCourseTitleHeight(courseHeaderRef.current.offsetHeight);
         if (ctaRef.current) {
            setCtaButtonHeight(ctaRef.current.offsetHeight);
         } else {
            setCtaButtonHeight(0);
         }
         const carousel = document.querySelector(
               '.carouselAndTitleAndArrowsContainer'
            ) as HTMLElement | null;

         if (carousel) {
            setCarouselHeight(carousel.offsetHeight);
         }
         prevNextRef.current &&
            setPrevNextHeight(prevNextRef.current.offsetHeight)
         lessonSurveyRef.current &&
            setLessonSurveyHeight(lessonSurveyRef.current.offsetHeight)

         setTimeout(() => {
            contentRef.current &&
               setContentHeight(contentRef.current.offsetHeight)
         }, 900);
      };
      handleResize();
      requestAnimationFrame(() => {
         handleResize();
      });
      window.addEventListener("scroll", handleResize);

      return () => {
         window.removeEventListener("scroll", handleResize);
      };
      // eslint-disable-next-line
   }, []);

   useEffect(() => {
      setCourses(accelerator.courses);
      setExpanded(currentLesson.courseID);
      setShowModal(false);
      setShowOrNotNextLessonModal(false);
      setShowPopupDialog(false);
      setShowVideoEnd(false);
      setVideoLessonCount(
         accelerator.courses.reduce((acc: Lesson[], next: Course) => {
            return [
               ...acc,
               ...next.lessons.filter(
                  (lesson) =>
                     !lesson.is_introduction && lesson.lesson_type === "video"
               ),
            ];
         }, []).length
      );
      setVideoLessons(
         accelerator.courses.flatMap(course =>
            course.lessons.filter(
               lesson =>
                  lesson.lesson_type === "video"
            )
         )
      );
      if (currentLesson.lesson_type == 'text' && isMobile) {
         const interval = setInterval(() => {
            const navElement = document.querySelector('.css-1cvsy14');
            console.log(navElement);
            if (navElement) {
               navElement.remove();
            }
            else clearInterval(interval);
         }, 100);
         const removePaddingInterval = setInterval(() => {
            const elements = document.querySelectorAll(".css-pygf1o, .css-8qxxkj");

            if (elements.length > 0) {
               elements.forEach((el) => {
                  (el as HTMLElement).style.padding = "0px";
               });
               clearInterval(removePaddingInterval);
            }
         }, 100);
         const setPositionInterval = setInterval(() => {
            const elements = document.querySelectorAll(".coursePlayer_customScrollbar__L1qEy");

            if (elements.length > 0) {
               elements.forEach((el) => {
                  (el as HTMLElement).style.position = "initial";
               });
               clearInterval(setPositionInterval);
            }
         }, 100);
         const removeMinHeightInterval = setInterval(() => {
            const elements = document.querySelectorAll(".coursePlayer_videoModalContainerTextType__9w1e6");
            if (elements.length > 0) {
               elements.forEach((el) => {
                  (el as HTMLElement).style.minHeight = "auto";
                  (el as HTMLElement).style.zIndex = "1";
               });
               clearInterval(removeMinHeightInterval);
            }
         }, 100);

         return () => { };
      }
      const acceleratorSlug = accelerator.url.split("/").filter(Boolean).pop();
      const lessonSlug = currentLesson.url.split("/").filter(Boolean).pop();
      const introSlugs = [
         "intro-about-this-module",
      ];
      if (
         acceleratorSlug === "straight-shootin-golf-2-0" &&
         introSlugs.includes(lessonSlug?.toString() || "")
      ) {
         setShowLessonSurveyBanner(true);
      } else {
         setShowLessonSurveyBanner(false);
      }
   }, [accelerator, currentLesson]);

   // useEffect(() => {
   //    const fetchUserStartedAllLessons = async () => {
   //       await userStartedAllLessons();
   //    }
   //    fetchUserStartedAllLessons();
   // }, [accelerator, currentLesson]);


   useEffect(() => {
      if(currentWatchTime && currentWatchTime > 0) {
         totalTimeWatched(
            currentWatchTime,
            accelerator,
            previouslyWatchedLesson
         );
      }
      setPreviouslyWatchedLesson(currentLesson)
      setIsVideoPlaying(false)
      if (currentLesson.video_has_gif && currentLesson.video_gif_file) {
         setShowGifOverlay(true)
      } else {
         setShowGifOverlay(false)
      }
      updateLessonCounter()
      setShowModal(false);
      setShowOrNotNextLessonModal(false);
      setShowPopupDialog(false);
      setShowVideoEnd(false);
      if (playerTitleRef.current)
         setPlayerTitleHeight(playerTitleRef.current.offsetHeight);
      setTimeout(() => {
         const carousel = document.querySelector(
            '.carouselAndTitleAndArrowsContainer'
         ) as HTMLElement | null;

         if (carousel) {
            setCarouselHeight(carousel.offsetHeight);
         }
      }, 50);
      prevNextRef.current &&
         setPrevNextHeight(prevNextRef.current.offsetHeight)
      lessonSurveyRef.current &&
         setLessonSurveyHeight(lessonSurveyRef.current.offsetHeight)
      if (currentLesson.lesson_type === "text") setVideoLoaded(true);

      const currentVideoLessonIndex = videoLessons.findIndex(
         lesson => lesson.ID === currentLesson.ID
      );
      const prev =
         currentVideoLessonIndex > 0 ? videoLessons[currentVideoLessonIndex - 1] : null;
      const next =
         currentVideoLessonIndex !== -1 && currentVideoLessonIndex < videoLessons.length - 1
            ? videoLessons[currentVideoLessonIndex + 1]
            : null;
      setNextVideoLesson(next);
      setPrevVideoLesson(prev);
      
   }, [currentLesson, popularCoursesOnly]);

   useEffect(() => {
      // if (playerTitleRef.current) {
      //    setPlayerTitleHeight(playerTitleRef.current.offsetHeight);
      // }
      containerRef.current &&
         setPlayerHeight(containerRef.current.offsetHeight);
      playerTitleRef.current &&
         setPlayerTitleHeight(playerTitleRef.current.offsetHeight);
      courseHeaderRef.current &&
         setCourseTitleHeight(courseHeaderRef.current.offsetHeight);
      if (ctaRef.current) {
         setCtaButtonHeight(ctaRef.current.offsetHeight);
      } else {
         setCtaButtonHeight(0);
      }

      const carousel = document.querySelector(
            '.carouselAndTitleAndArrowsContainer'
         ) as HTMLElement | null;

      if (carousel) {
         setCarouselHeight(carousel.offsetHeight);
      }
      prevNextRef.current &&
         setPrevNextHeight(prevNextRef.current.offsetHeight)
      lessonSurveyRef.current &&
         setLessonSurveyHeight(lessonSurveyRef.current.offsetHeight)
      
   }, [videoLoaded]);

   useEffect(() => {
      if (
         !lessonComplete.isIntroduction &&
         lessonComplete.lessonID &&
         !completeLessonsID.includes(lessonComplete.lessonID)
      ) {
         completeLessonsID.push(lessonComplete.lessonID);
      }
      if (isFirstRender.current) {
         isFirstRender.current = false;
         return;
      }
      if (!isUserSawBanner) {
         updateBannerVisibilityy();
      }
      // eslint-disable-next-line
   }, [lessonComplete]);

   const endLesson = () => {
      setLessonComplete({
         lessonID: currentLesson.ID,
         courseID: currentLesson.courseID,
         wasComplete: currentLesson.complete,
         isIntroduction: !!currentLesson.is_introduction,
      });
      let currentCourse = null;
      if (!currentLesson.courseID && !currentLesson.is_introduction) {
         currentCourse = courses.find((el) =>
            el.lessons.find((lesson) => lesson.ID === currentLesson.ID)
         );
      } else {
         currentCourse = courses.find((el) => el.ID === currentLesson.courseID);
      }

      let nextCourseLesson = null;
      let currentCourseIndex = null;
      if (currentCourse) {
         // If autoplay is enabled, only look for video lessons, skip text lessons
         if (autoplayNextForCourse) {
            nextCourseLesson = currentCourse.lessons.find(
               (el, index) =>
                  currentLesson.ordinal + 1 <= index &&
                  el.lesson_type === "video"
            );
         } else {
            // If autoplay is disabled, include both video and text lessons
            nextCourseLesson = currentCourse.lessons.find(
               (el, index) =>
                  currentLesson.ordinal + 1 === index &&
                  (el.lesson_type === "video" || el.lesson_type === "text")
            );
         }
         currentCourseIndex = courses.indexOf(currentCourse);
      }
      if (nextCourseLesson) {
         showNextLessonInCourseModal(nextCourseLesson);
         if (currentLesson.url.split("/").filter(Boolean).pop() === "welcome-to-pin-high-approach-shots" || currentLesson.url.split("/").filter(Boolean).pop() === "welcome-to-the-green-stopping-power-protocol") {
            setShowSwingSurveydDialog(true);
         }
      } else if (
         currentCourseIndex !== null &&
         courses.length > currentCourseIndex + 1
      ) {
         for (let i = currentCourseIndex + 1; i < courses.length; i++) {
            let newCourse = courses[i];
            // If autoplay is enabled, only look for video lessons in next courses
            if (autoplayNextForCourse) {
               nextCourseLesson = newCourse.lessons.find(
                  (el) => el.lesson_type === "video"
               );
            } else {
               nextCourseLesson = newCourse.lessons.find(
                  (el) => el.lesson_type === "video" || el.lesson_type === "text"
               );
            }
            if (nextCourseLesson) {
               showNextLessonInCourseModal(nextCourseLesson);
               break;
            }
         }
      }
   };

   const showNextLessonInCourseModal = (lesson: Lesson) => {
      // if #2 in URL not show modal
      const endsWithHash2 = /#2\/?$/.test(window.location.href);
      if (endsWithHash2) {
         return;
      }
      if (isMobile) {
         setShowModal(false);
         //return;
      };
      let champCount = parseInt(localStorage.getItem('nextModalChampCounter') || "0");
      champCount++;
      localStorage.setItem(`nextModalChampCounter`, champCount.toString());
      //slobodan
      const hasChamp = window.pgUser?.products?.some((product: string) => product === '125' || product === '124')
      if (champCount % 4 === 0 && !hasChamp) {
         setCurrentPromo(CHAMPION_PASS_PROMO)
         console.log("next is showing champ pass!!!!!!")
      }
      setModalTitle(
         isMobile
            ? MOVE_TO_NEXT_LESSON_MOBILE
            : currentLesson.cta
               ? MOVE_TO_VIP
               : MOVE_TO_NEXT_LESSON
      );
      setModalBtnText(lesson.name);
      setModalAction(() => () => playNextLesson(lesson));
      //setShowModal(true);
      setShowOrNotNextLessonModal(true);
   };

   const playNextLesson = (lesson: Lesson, autoplay: boolean = true) => {
      // Check if this is a playlist course and generate appropriate URL
      let navigationUrl = removeDomainFromUrl(lesson.url);

      if (accelerator.course_type === CourseType.Playlist || navigationUrl.startsWith('/lessons/')) {
         // Extract lesson slug from original URL
         const lessonSlug = lesson.url.split('/').filter(Boolean).pop();
         // Extract current course slug from current URL
         const currentPath = window.location.pathname;
         const pathSegments = currentPath.split('/').filter(Boolean);

         if (pathSegments.length >= 2 && pathSegments[0] === 'courses') {
            const playlistCourseSlug = pathSegments[1];
            navigationUrl = `/courses/${playlistCourseSlug}/${lessonSlug}/`;
         }
      }

      navigate(navigationUrl);
      setShowModal(false);
      setShowOrNotNextLessonModal(false);
      setShowPopupDialog(false);
      setShowVideoEnd(false);

      if(autoplay) {
         setTimeout(() => {
            playLessonButton();
            setIsVideoPlaying(true)
         }, 1000);
      }
   };
   const renderPlaylist = courses.map((course, index) => {
      return (
         <CoursePlaylist
            setPreventAutoScroll={setPreventAutoScroll}
            tmpHeaderHeight={tmpHeaderHeight}
            courseHeaderRef={courseHeaderRef.current}
            ctaRef={ctaRef}
            totalVideoPlayerHeight={totalVideoPlayerHeight}
            handleIsShowChampionsPassModal={handleIsShowChampionsPassModal}
            moduleIndex={index}
            updateLessonCounter={updateLessonCounter}
            key={index}
            course={course}
            currentLesson={currentLesson}
            lessonComplete={lessonComplete}
            setShowModal={(value: boolean) => setShowModal(value)}
            setAcceleratorProgress={(value: any) => setAcceleratorProgress(value)}
            expanded={expanded}
            setExpanded={setExpanded}
            scrollbarsRef={scrollbarsRef.current}
            playerHeight={
               playerHeight +
               playerTitleHeight +
               courseTitleHeight +
               contentHeight +
               (currentLesson.cta ? ctaButtonHeight : 0)
            }
            // @ts-ignore
            playerRef={containerRef}
            videoLoaded={videoLoaded}
            accelerator={accelerator}
         />
      );
   });

   const hideTitle = () => {
      setHiding(false);
   };

   const showTitle = () => {
      setHiding(true);
   };

   const [isVideoPlaying, setIsVideoPlaying] = useState(false);
   const [firstVideoPlay, setFirstVideoPlay] = useState(false);

   const handleVideoPlay = async () => {
      setIsVideoPlaying(true);
      hideTitle();
      // if (isMobile) {
      //    setExpanded(currentLesson.courseID);
      //    setTimeout(() => {
      //       const playingLessonId = currentLesson.ID;
      //       const playingLessonElement = document.querySelector(`#lesson-id-${playingLessonId}`)
      //       if (playingLessonElement) {
      //          console.log("playing lesson el: ", playingLessonElement);
      //          const elementTop = playingLessonElement.getBoundingClientRect().top + window.scrollY;
      //          const offset = totalVideoPlayerHeight || 0; 
      //          window.scrollTo({
      //             top: elementTop - offset,
      //             behavior: "smooth",
      //          });
      //       }
      //    }, 300);
      // }
      console.log("fist video play >> ", firstVideoPlay + "   <<>>> isvideo playing >> ", isVideoPlaying)
      if (!firstVideoPlay) {
         const currentDate = getCurrentDate();
         const updatedCount = videoCompletionCount + 1;

         // Update the count in state and localStorage
         setVideoCompletionCount(updatedCount);
         localStorage.setItem(`videoCompletionCount_${currentDate}`, updatedCount.toString());
         setFirstVideoPlay(true);
      }
      let flags: any = window.pgUser.flags || [];
      let startedCourses: any = flags.find((item: any) => item.startedCourses !== undefined);
      if (!startedCourses) startedCourses = { startedCourses: {} };
      startedCourses.startedCourses[window.courseId] = true;
      await handlePostAxiosRequest(startedCourses);
   };

   const handleVideoPause = () => {
      setIsVideoPlaying(false);
      showTitle();
   };

   const [currentWatchTime, setCurrentWatchTime] = useState<number>(0);
   useEffect(() => {
      let interval: ReturnType<typeof setInterval> | null = null;

      if (isVideoPlaying) {
         interval = setInterval(() => {
            setCurrentWatchTime(prev => prev + 1);
         }, 1000);
      } else {
         setCurrentWatchTime(0);
         if (interval) clearInterval(interval);
      }

      return () => {
         if (interval) clearInterval(interval);
      };
   }, [isVideoPlaying]);

   const trackWatchTime = () => {
      if (currentWatchTime && currentWatchTime > 0) {
         totalTimeWatched(
            currentWatchTime,
            accelerator,
            currentLesson
         );
      }
   };
   useEffect(() => {
      const handlePageHide = () => {
         trackWatchTime();
      };

      window.addEventListener('pagehide', handlePageHide);

      return () => {
         window.removeEventListener('pagehide', handlePageHide);
      };
   }, [currentWatchTime]);

   const [currentPromo, setCurrentPromo] = useState<Promo | null>(null);

   const shuffleArray = (array: Promo[]): Promo[] => {
      if (!array) return [];
      const newArray: Promo[] = array.slice();
      for (let i = newArray.length - 1; i > 0; i--) {
         const j = Math.floor(Math.random() * (i + 1));
         [newArray[i], newArray[j]] = [newArray[j], newArray[i]];
      }
      return newArray;
   };

   const [shuffledPromos, setShuffledPromos] = useState<Promo[]>([]);
   const [promoIndex, setPromoIndex] = useState<number>(0);
   const [resetBanner, setVideoBanner] = useState<boolean>(false);

   useEffect(() => {
      // Shuffle the promo array when it changes or on initial render
      if (!window.pgPromo) return;
      const updatedPromos = window.pgPromo || dummyPgPromo
      setShuffledPromos(shuffleArray(updatedPromos));
      // Reset promo index
      setPromoIndex(0);
   }, [window?.pgPromo]);

   useEffect(() => {
      // Get the current promo from the shuffled array
      const currentPromo = shuffledPromos[promoIndex];
      setPromoIndex((prevIndex) => prevIndex + 1);

      // If all promos have been shown, shuffle the array again
      if (promoIndex === shuffledPromos.length) {
         if (!window.pgPromo) return;
         const updatedPromos = window.pgPromo
         setShuffledPromos(shuffleArray(updatedPromos));
         setPromoIndex(0);
      }

      // Set the current promo in the state

      setCurrentPromo(currentPromo);

   }, [shuffledPromos, currentLesson, resetBanner, addToPlaylist]);

   useEffect(() => {
      const handlePageReload = (event: any) => {
         if (isMobile) {
            window.location.reload();
         }
      };

      window.addEventListener('popstate', handlePageReload);


      return () => {
         window.removeEventListener('popstate', handlePageReload);
      }
   }, [currentLesson.ID]);

   const [videoCompletionCount, setVideoCompletionCount] = useState(0);
   const [viewProductCalled, setViewProductCalled] = useState(false);
   const [showVideoEnd, setShowVideoEnd] = useState(false);
   const [prevVideoId, setPrevVideoId] = useState<number | null>(null);
   const [oldStoredCount, setOldStoredCount] = useState(0);

   const getCurrentDate = () => {
      const now = new Date();
      const year = now.getFullYear();
      const month = String(now.getMonth() + 1).padStart(2, "0");
      const day = String(now.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
   };

   const playLesson = (lesson: Lesson) => {
      const playingLessonId = lesson.ID;
      const playingLessonElement = document.querySelector(`#lesson-id-${playingLessonId}`);
      if (playingLessonElement && !isMobile) {
         playingLessonElement?.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
            inline: 'start',
         });
      } else {
         const lessonElement = document.getElementById(`lesson-id-${currentLesson.ID}`);

         lessonElement?.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'start' });
      }

      // Check if this is a playlist course and generate appropriate URL
      let navigationUrl = removeDomainFromUrl(lesson.url);

      if (accelerator.course_type === CourseType.Playlist) {
         // Extract lesson slug from original URL
         const lessonSlug = lesson.url.split('/').filter(Boolean).pop();
         // Extract current course slug from current URL
         const currentPath = window.location.pathname;
         const pathSegments = currentPath.split('/').filter(Boolean);

         if (pathSegments.length >= 2 && pathSegments[0] === 'courses') {
            const playlistCourseSlug = pathSegments[1];
            navigationUrl = `/courses/${playlistCourseSlug}/${lessonSlug}/`;
         }
      }

      navigate(navigationUrl);

   }
   const playLessonButton = () => {
      if (isMobile || isIOS()) return;
      if (
         (!window.is_autoplay && !autoplayNextForCourse
            ||
            window.is_autoplay && window.is_autoplay.indexOf("" + accelerator.ID + "false") !== -1)
      ) {
         return;
      }
      if (!vimeoPlayerRef.current) {
         console.warn("Vimeo player ref is not ready");
         return;
      }
      const iframeEl = vimeoPlayerRef.current.player?.element;
      if (!iframeEl) {
         console.warn("Vimeo iframe element not found");
         return;
      }
      const player = new Player(iframeEl);
      player.play();
   }

   const handleSubmit = () => {
      setShowSwingSurveydDialog(false);
      if (selected) {
         const selectedLesson = accelerator.courses
            .flatMap(course => course.lessons)
            .find(lesson => lesson.ID === selected);
         if (selectedLesson?.url) {
            playLesson(selectedLesson);
         }
      }
   };

   useEffect(() => {
      const currentDate = getCurrentDate();
      const storedCount = parseInt(localStorage.getItem(`videoCompletionCount_${currentDate}`) || "0", 10) || 0;
      setVideoCompletionCount(storedCount);

   }, [videoCompletionCount, acceleratorProgress, currentPromo]);


   useEffect(() => {

      const currentDate = getCurrentDate();
      const storedCount = parseInt(localStorage.getItem(`videoCompletionCount_${currentDate}`) || "0", 10) || 0;
      const lessonID = parseInt(localStorage.getItem('modalLessonID') || "0");

      let champCount = parseInt(localStorage.getItem('champFourCounter') || "0");
      // Check if the count is 6 and show the modal
      if (storedCount !== 0 && storedCount % 6 === 0 && currentPromo && (lessonID === currentLesson.ID || lessonID === 0) && storedCount !== oldStoredCount) {
         localStorage.setItem(`modalLessonID`, currentLesson.ID.toString());
         champCount++;
         let triggerSplitCh = false;
         const hasChampPass = window.pgUser?.products?.some((product: string) => product === '124' || product === '125')
         if (champCount === 4 && !hasChampPass) {
            console.log("champcount 4!!!!")
            const updatedPromos = CHAMPION_PASS_PROMO
            setCurrentPromo(updatedPromos);
            champCount = 0;
            triggerSplitCh = true;
         }
         if (videoLoaded) {
            setShowModal(false);
            setShowOrNotNextLessonModal(false);

            if (triggerSplitCh) {
               if (window.championsPassTrialPricing) {
                  viewChampionPassModal('Video', 'Pop Up', '', "In Video", currentPromo.name, "WE603");
               } else {
                  viewChampionPassModal('Video', 'Pop Up', '', "In Video", currentPromo.name, null);
               }
               triggerSplitCh = false;
            }
         }
         localStorage.setItem('champFourCounter', champCount.toString());
      } else {
         localStorage.setItem(`modalLessonID`, "0");
         setOldStoredCount(storedCount)
      }

      if (acceleratorProgress === 100 && currentPromo) {
         //setAddToPlaylist(true);
         setCompletedCourse(true);

         if (videoLoaded) {
            //setShowPopupDialog(true);
            setShowModal(false);
            setShowOrNotNextLessonModal(false);
         }
      }
   }, [currentLesson.ID]);

   useEffect(() => {

      setFirstVideoPlay(false);

   }, [currentLesson.ID])


   useEffect(() => {
      // Trigger viewProduct only if it hasn't been called yet
      if (viewProductCalled && showModal && !showPopupDialog && currentPromo) {
         viewProduct("Video", "Banner", "", "End of Video", currentPromo.name);
      }
      setViewProductCalled(false)
   }, [viewProductCalled]);


   // Assuming courses is an array of course objects and each course has an array of lessons
   courses.forEach(course => {
      course.lessons.forEach(lesson => {
         if (lesson.cta) {
            lesson.cta.url = lesson.cta.url.replace(
               "first_name=&",
               `first_name=${window.pgUser?.first_name}&`
            );
            lesson.cta.url = lesson.cta.url.replace(
               "last_name=&",
               `last_name=${window.pgUser?.last_name}&`
            );
            lesson.cta.url = lesson.cta.url.replace(
               "email=&",
               `email=${window.pgUser?.user_email}&`
            );
         }
      });
   });


   //scroll upnext in playlist
   useEffect(() => {
      const scrollToFinalOffer = () => {
         if (scrollbarsRef.current) {
            //@ts-ignore
            scrollbarsRef.current.scrollToBottom();
         } else {
            console.error("upNextPlaylistRef.current is null desktop");
         }
      };

      const scrollMobileOffer = () => {
         setTimeout(() => {
            const upNextElement = document.getElementById("upNext");
            if (upNextElement) {
               // upNextElement.scrollIntoView({ behavior: 'smooth', block: 'end' });
               const headerHeight =
                  playerHeight + playerTitleHeight + courseTitleHeight;
               const elementPos = upNextElement.offsetTop - headerHeight;
               // const scrollToPosition = elementPos > 800? elementPos + headerHeight : elementPos - headerHeight;
               const scrollToPosition = elementPos;
               console.log("element top >> ", upNextElement.offsetTop);
               console.log("position to scroll >> ", scrollToPosition);

               window.scroll({ top: scrollToPosition, behavior: "smooth" });
            }
         }, 500);
      };

      !isMobile && scrollToFinalOffer();
      isMobile && scrollMobileOffer();
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [addToPlaylist]);

   useEffect(() => {
      setTimeout(() => {
         if (!isMobile) {
            const lessonElement = document.getElementById(`lesson-id-${currentLesson.ID}`);
            const scrollContainer = document.querySelector('.ScrollbarsCustom') || document.querySelector('.coursePlaylistLessons');

            if (lessonElement && scrollContainer) {
               const hasScrolled = scrollContainer.getAttribute('data-scrolled') === 'true';

               let scrollToElement = lessonElement.previousElementSibling || lessonElement;
               if (!hasScrolled) {
                  if (onlyMobileRange) {
                     lessonElement.scrollIntoView({ behavior: 'smooth', block: 'end', inline: 'start' });
                     setTimeout(() => {
                        window.scrollBy(0, 45);
                     }, 1000);
                  } else if (tabletsRange) {
                     lessonElement.scrollIntoView({ behavior: 'smooth', block: 'end', inline: 'start' });
                     setTimeout(() => {
                        window.scrollBy(0, 45);
                     }, 1000);
                  } else if (smallScreenRange) {
                     lessonElement.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'start' });
                  } else {
                     scrollToElement.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'start' });
                  }
                  scrollContainer.setAttribute('data-scrolled', 'true');
               }
            }
         }
      }, 800);
   }, [currentLesson.ID]);

   const fixedElementRef = useRef<HTMLDivElement>(null);
   const paddingElementRef = useRef<HTMLDivElement>(null);
   const [paddingHeight, setPaddingHeight] = useState(0);
   const [wpadminbarPadding, setWpadminbarPadding] = useState(0);

   useEffect(() => {
      const wpadminbarelement = document.getElementById('wpadminbar');
      if (wpadminbarelement) {
         setWpadminbarPadding(46);
         if (fixedElementRef.current) {
            fixedElementRef.current.style.top = `${wpadminbarPadding}px`;
         }
      } else {
         setWpadminbarPadding(0);
      }
      const handleScroll = () => {
         if (fixedElementRef.current && paddingElementRef.current) {
            const scrollTop = window.scrollY - wpadminbarPadding;
            const fixedElementHeight = fixedElementRef.current.clientHeight;
            const finalPaddingHeight = fixedElementHeight + 20;

            setPaddingHeight(finalPaddingHeight);
            fixedElementRef.current.style.top = `${-scrollTop}px`;
         }
      };
      if (fixedElementRef.current && paddingElementRef.current) {
         const fixedElementHeight = fixedElementRef.current.clientHeight;
         const finalPaddingHeight = fixedElementHeight + 20;
         setPaddingHeight(finalPaddingHeight);
      }

      window.addEventListener('scroll', handleScroll);
      return () => {
         window.removeEventListener('scroll', handleScroll);
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [fixedElementRef.current && fixedElementRef.current.innerHTML, wpadminbarPadding]);

   const updateBannerVisibilityy = useCallback(async () => {
      // @ts-ignore
      const courseId = window.courseId;
      const res = await axiosRequest("GET", `/wp-json/pg/v2/course/${courseId}`);
      const response = res.data.data as Accelerator;
      if (calculateWatchedLessons(response) === 3) {
         await axiosRequestData("POST", "/wp-json/pg/v1/user/sawbanner")
            .then(() => {
               setIsUserSawBanner(true)
               setIsShowBanner(true);
               setTimeout(() => {
                  setIsShowBanner(false)
               }, 13000)
            })
            .finally(() => { });

      }
   }, []);


   const [contents, setContents] = useState<string[]>([]);
   const [content, setContent] = useState('');

   useEffect(() => {
      setNextCourseLesson({ name: "", url: "" });

      let currentCourse = null;
      if (!currentLesson.courseID && !currentLesson.is_introduction) {
         currentCourse = courses.find((el) =>
            el.lessons.find((lesson) => lesson.ID === currentLesson.ID)
         );
      } else {
         currentCourse = courses.find((el) => el.ID === currentLesson.courseID);
      }

      let nextCourseLesson = null;
      let currentCourseIndex = null;
      if (currentCourse) {
         // If autoplay is enabled, only look for video lessons, skip text lessons
         if (autoplayNextForCourse) {
            nextCourseLesson = currentCourse.lessons.find(
               (el, index) =>
                  currentLesson.ordinal + 1 <= index &&
                  el.lesson_type === "video"
            );
         } else {
            nextCourseLesson = currentCourse.lessons.find(
               (el, index) =>
                  currentLesson.ordinal + 1 === index &&
                  (el.lesson_type === "video" || el.lesson_type === "text")
            );
         }
         currentCourseIndex = courses.indexOf(currentCourse);
      }
      if (nextCourseLesson) {
         setNextCourseLesson(nextCourseLesson);
      } else if (
         currentCourseIndex !== null &&
         courses.length > currentCourseIndex + 1
      ) {
         for (let i = currentCourseIndex + 1; i < courses.length; i++) {
            let newCourse = courses[i];
            // If autoplay is enabled, only look for video lessons in next courses
            if (autoplayNextForCourse) {
               nextCourseLesson = newCourse.lessons.find(
                  (el) => el.lesson_type === "video"
               );
            } else {
               nextCourseLesson = newCourse.lessons.find(
                  (el) => el.lesson_type === "video" || el.lesson_type === "text"
               );
            }
            if (nextCourseLesson) {
               setNextCourseLesson(nextCourseLesson);
               break;
            }
         }
      }
      const repsonsiveForButton = () => {
         const vipButton = document.getElementById('VipButton');
         const contentBullet = document.getElementById('contentBullet');
         if (vipButton) {
            if (isMobile) {
               window.scroll({ top: 1, behavior: "smooth" });
               vipButton.style.width = '100%';
               if (contentBullet) {
                  contentBullet.style.paddingLeft = '32px';
               }
            } else {
               vipButton.style.width = '160px';
            }
         }
         if (currentLesson) {
            const splittedContent = currentLesson.content.split("***mobile video***");
            setContents(splittedContent);
            const updatedContent = currentLesson.content.replace("***mobile video***", "");
            setContent(updatedContent);
         }
         const secondContent = document.getElementById('secondContent');
         const textUnderButton = document.getElementById('textUnderButton');
         if (secondContent) {
            secondContent.style.width = '100%';
            if (isMobile) {
               if (currentLesson.name.indexOf("Want") !== -1) {
                  secondContent.style.padding = "0 20px";
               }
            }
         }
      };

      var timeoutId = setTimeout(repsonsiveForButton, 100);
      timeoutId = setTimeout(repsonsiveForButton, 500);
      timeoutId = setTimeout(repsonsiveForButton, 1000);
      return () => clearTimeout(timeoutId);
   }, [isMobile, currentLesson]);


   useEffect(() => {
      const attachClickListener = () => {
         const vipButton = document.getElementById('VipButton');
         if (vipButton) {
            vipButton.addEventListener('click', handleClick);
         }
      };

      const handleClick = () => {
         clickedVipGolfSchool(accelerator, currentLesson, "RSVP Content Page")
      };

      const timeoutId = setTimeout(attachClickListener, 1000);

      return () => clearTimeout(timeoutId);
   }, [currentLesson]);

   const continueBar = () => (
      <Grid
         container
         md={12}
         xs={12}
         sx={{
            backgroundColor: "#EFEFEF",
            padding: "12px 20px",
            marginTop: isMobile ? "25px" : null,
            zIndex: "10",
            height: "fit-content",
         }}
      >
         <Grid item md={9} xs={9}>
            <Box>
               <Typography
                  sx={{
                     paddingBottom: "9px",
                     fontSize: 14,
                     lineHeight: "14px",
                     margin: 0,
                     fontWeight: 900,
                     color: "#355ADD",
                  }}
               >
                  Watch Next Playlist Video:
               </Typography>
            </Box>
            <Box>
               <Typography
                  sx={{
                     fontSize: 14,
                     lineHeight: "14px",
                     margin: 0,
                     fontWeight: 400,
                     color: "#18181B",
                  }}
               >
                  {parse(nextCourseLesson.name)}
               </Typography>
            </Box>
         </Grid>
         <Grid item md={3} xs={3}>
            <Button
               onClick={() => {
                  // Generate playlist-aware URL, same logic as playLesson function
                  let navigationUrl = removeDomainFromUrl(nextCourseLesson.url);

                  if (accelerator.course_type === CourseType.Playlist) {
                     // Extract lesson slug from original URL
                     const lessonSlug = nextCourseLesson.url.split('/').filter(Boolean).pop();
                     // Extract current course slug from current URL
                     const currentPath = window.location.pathname;
                     const pathSegments = currentPath.split('/').filter(Boolean);

                     if (pathSegments.length >= 2 && pathSegments[0] === 'courses') {
                        const playlistCourseSlug = pathSegments[1];
                        navigationUrl = `/courses/${playlistCourseSlug}/${lessonSlug}/`;
                     }
                  }

                  window.location.href = navigationUrl;
               }}
               fullWidth
               sx={{
                  zIndex: "0 !important",
                  borderRadius: "3px",
                  height: "34px",
               }}
               className={styles.ctaButton}
            >
               <Typography
                  sx={{
                     fontSize: 16,
                     lineHeight: "22px",
                     margin: 0,
                     fontWeight: 700,
                     color: "white",
                  }}
               >
                  Continue
               </Typography>
            </Button>
         </Grid>
      </Grid>
   );
   const newSSTSUser = useMemo(() => {
      return accelerator.ID === 65201 && accelerator.progress === 0;
   }, [accelerator.ID, accelerator.progress]);
   const labelsFirstModule = [
      "Thin/Topped Shot",
      "Fat/Heavy/Behind the ball",
      "Slice or Miss Right",
      "Hook or Miss Left",
      "Distance",
      "Low Height/Trajectory",
      "High, Spinny Shots"
   ];
   const labelsSecondModule = [
      "Slower swing speed",
      "Off-center strikes off the toe",
      "Off-center strikes off the heel",
      "Slice/right miss",
      "Hook/miss left",
      "Fat shots/hitting behind the ball",
      "Hitting it thin/topping it",
      "I hit the ball too low",
      "I hit the ball too high",
      "Fundamentals (grip, alignment, setup, ball position)",
   ];
   const labelsStraightShootin = [
      'Contact',
      'Speed And Distance',
      'Accuracy And Shot Control'
   ];
   return (
      <>
         {/* { 
            showGuidedJourney && newSSTSUser && <GuidedJourneyModal /> 
         } */}
         {(
            <TakeSwingSurveyDialog
               show={showSwingSurveydDialog}
               options={accelerator?.courses?.[0]?.lessons.slice(1)}
               setShow={setShowSwingSurveydDialog}
               labels={currentLesson.url.split("/").filter(Boolean).pop() === "welcome-to-pin-high-approach-shots" ?
                  labelsFirstModule : labelsSecondModule
               }
               selected={selected}
               setSelected={setSelected}
               title={currentLesson.url.split("/").filter(Boolean).pop() === "welcome-to-pin-high-approach-shots" ? "What's Your Most Common Miss with Your Hybrids and Fairway Woods?" : "What do you Believe is Costing you the Most Distance?"}
               onSubmit={() => {
                  handleSubmit();
               }}
            />
         )}
         {
            <LessonSurvey
               show={showLessonSurvey}
               options={accelerator?.courses}
               setShow={setShowLessonSurvey}
               locationForMixpanel={locationForMixpanel}
               labels={labelsStraightShootin}
               selected={selected}
               setSelected={setSelected}
               title={"Which area of the game do you want your Zone Of Squareness to improve your game first - contact, speed and distance, or accuracy and shot control"}
               onSubmit={() => {
                  handleSubmit();
               }}
            />
         }
         <Grid
            container
            columnSpacing={3}
            sx={{
               zIndex: 5,
               marginBottom: 0,
               flexFlow: { xs: "column", md: "row" }
            }}
         >
            <Grid
               item
               xs={12}
               md={8}
               onMouseEnter={showTitle}
               onMouseLeave={hideTitle}
               sx={{ zIndex: 1 }}
            >
               <Box
                  className={currentLesson.lesson_type === "text" && isMobile ? styles.videoModalContainerTextType : styles.videoModalContainer}
                  ref={containerRef}
                  sx={{
                     position: { xs: currentLesson.lesson_type !== "text" ? "sticky" : "relative", md: "relative" },
                     top: 0,
                     scrollMarginTop: playerTitleHeight,
                     marginBottom: playerTitleHeight + "px",
                     height: currentLesson.lesson_type === "text" ? "100%" : 'auto'
                  }}
               >
                  {showOrNotNextLessonModal && <NextLessonModal
                     playerTitleHeight={playerTitleHeight}
                     playerHeight={playerHeight}
                     accelerator={accelerator}
                     modalTitle={modalTitle}
                     modalAction={modalAction}
                     modalBtnText={modalBtnText}
                     currentLesson={currentLesson}
                     promo={currentPromo && currentPromo}
                     callback={(showOrNotNextLessonModal: boolean) => {
                        setShowModal(showOrNotNextLessonModal);
                     }}
                     handleReplay={handleReplay}
                     autoplayNextForCourse={autoplayNextForCourse}
                     setAutoplayNextForCourse={setAutoplayNextForCourse}
                  />}
                  {currentLesson.lesson_type === "video" && (
                     <Typography
                        textTransform={"uppercase"}
                        ref={playerTitleRef}
                        fontSize={{ xs: "12px", md: "14px" }}
                        fontFamily={'Repro Mono'}
                        style={{ display: videoLoaded ? "block" : "none" }}
                        m={0}
                        p={1}
                        color={"#ffffff"}
                        textAlign={"center"}
                        sx={{
                           position: "absolute",
                           backgroundColor: {
                              xs: "rgba(0, 0, 0, 0.8) ",
                              md: "#1D1A1A",
                           },
                           ...(isVideoPlaying && {
                              opacity: !hiding && !isMobile ? 0 : 1,
                           }),
                           ...(!isVideoPlaying && { opacity: 1 }),
                        }}
                        id={`lesson-banner-${currentLesson.ID}`}
                     >
                        <Box
                           component={"img"}
                           sx={{ marginRight: "7px", paddingBottom: "2px" }}
                           src="https://www.performancegolf.com/wp-content/uploads/2023/11/Video-Icon.png"
                           alt="play"
                        />
                        {parse(currentLesson.name)}{" "}
                        {currentLesson.duration !== "00:00" &&
                           "(" + currentLesson.duration + ")"}
                     </Typography>
                  )}

                  {currentLesson.lesson_type !== "text" && currentLesson.video?.ID && (
                     <div>
                        <Vimeo
                           key={`vimeo-${currentLesson.video?.ID}-${showOrNotNextLessonModal}`}
                           controls={showOrNotNextLessonModal ? false : true}
                           color="fd3300"
                           start={currentLesson.video.progress}
                           showTitle={false}
                           video={currentLesson.video?.ID}
                           responsive={true}
                           playsInline={true}
                           autoplay={false}
                           onLoaded={(event) => {
                              setTimeout(() => setVideoLoaded(true), 800);
                           }}
                           onTimeUpdate={onProgressUpdate}
                           onEnd={(event: EndEvent) => {
                              setVideoBanner(false);
                              endLesson();
                              let vip =
                                 currentLesson.lesson_type === "cta" ||
                                    currentLesson.cta
                                    ? "Yes"
                                    : "No";
                              lessonVideoCompleteEvent(
                                 event.seconds,
                                 accelerator,
                                 currentLesson,
                                 vip,
                              );

                              setShowVideoEnd(true);
                              setPrevVideoId(currentLesson.video?.ID);
                              const lessonSlug = currentLesson.url.split("/").filter(Boolean).pop();
                              const introSlugs = [
                                 "intro-about-this-module",
                              ];
                              if (accelerator.url.split("/").filter(Boolean).pop() === "straight-shootin-golf-2-0" && introSlugs.includes(lessonSlug?.toString() || "")) {
                                 setShowLessonSurvey(true);
                                 setLocationForMixpanle("End Video Modal");
                              }
                           }}
                           onPause={(event: PauseEvent) => {
                              handleVideoPause();
                              if (
                                 event.seconds &&
                                 event.seconds.toFixed(0) !==
                                 event.duration.toFixed(0)
                              ) {
                                 pausedVideoEvent(
                                    event.seconds,
                                    accelerator,
                                    currentLesson
                                 );
                              }
                              if(currentWatchTime && currentWatchTime > 0) {
                                 totalTimeWatched(
                                    currentWatchTime,
                                    accelerator,
                                    currentLesson
                                 );
                              }
                           }}
                           onPlay={(event: PlayEvent) => {
                              handleVideoPlay();
                              // setTimeout(() => {
                              //    checkIfUserStartedAllLessons();
                              // }, 10000);
                              if (event) {
                                 let vip = currentLesson.lesson_type === "cta" || currentLesson.cta ? "Yes" : "No";
                                 startedVideoEvent(event.seconds, accelerator, currentLesson, vip);
                                 setShowVideoEnd(false);
                                 if (prevVideoId === currentLesson.video?.ID) {
                                    setVideoBanner(true);
                                    setShowModal(false);
                                    setShowOrNotNextLessonModal(false);
                                    setShowPopupDialog(false);
                                 }
                                 checkCurretViewMode();
                              }
                           }}
                           style={{
                              display: videoLoaded ? "block" : "none",
                              position: "absolute",
                              height: "100%",
                              width: "100%",
                              top: playerTitleHeight,
                              left: 0,
                              background: "#ffffff",
                           }}
                           ref={vimeoPlayerRef}
                        />
                        {showGifOverlay && (
                           <div className={styles['video-gif-overlay']} style={{ top: playerTitleHeight }}>
                              <img src={currentLesson.video_gif_file} alt="Intro GIF" className={styles['video-gif-overlay__img']} />
                              <button
                                 className={styles['video-gif-overlay__play']}
                                 onClick={(event) => {
                                    setShowGifOverlay(false);
                                    if (!vimeoPlayerRef.current) {
                                       console.warn("Vimeo player ref is not ready");
                                       return;
                                    }
                                    const iframeEl = vimeoPlayerRef.current.player?.element;
                                    if (!iframeEl) {
                                       console.warn("Vimeo iframe element not found");
                                       return;
                                    }
                                    const player = new Player(iframeEl);
                                    player.play();
                                 }}>
                              </button>
                           </div>
                        )}
                     </div>
                  )}

                  {currentLesson.lesson_type === "text" && (
                     <Grid ref={contentRef} container md={12} xs={12} sx={{ overflowY: isMobile ? 'none' : 'auto', borderTop: isMobile ? '1px solid #D9D9D9' : 'none' }} className={styles.customScrollbar}>
                        <Grid height={'max-content'} container md={12} xs={12} sx={{ paddingBottom: isMobile ? '20px' : 0 }}>
                           {isMobile && nextCourseLesson.ID &&
                              continueBar()
                           }
                           <Grid ref={fixedElementRef} item md={12} xs={12} sx={{
                              backgroundColor: '#000000',
                              opacity: 0.8,
                              padding: isMobile ? '20px' : '28px',
                              marginTop: isMobile && nextCourseLesson.ID ? '25px' : null,
                              zIndex: '10',
                              height: 'fit-content'
                           }}>
                              <Box>
                                 <Typography
                                    sx={{
                                       fontSize: 22,
                                       lineHeight: "25px",
                                       margin: 0,
                                       fontWeight: 700,
                                       color: 'white'
                                    }}
                                 >
                                    {parse(currentLesson.name)}
                                 </Typography>
                              </Box>
                              <Box>
                                 <Typography
                                    sx={{
                                       fontSize: 16,
                                       lineHeight: "25px",
                                       margin: 0,
                                       fontWeight: 700,
                                       color: 'white'
                                    }}
                                 >
                                    {parse(currentLesson.video.subheading)}
                                 </Typography>
                              </Box>
                           </Grid>
                           <Grid ref={paddingElementRef}
                              item md={6} xs={12} sx={{
                                 overflow: isMobile ? 'auto' : 'hidden',
                                 padding: '0 20px',
                                 paddingLeft: isMobile ? '20px' : '28px',
                                 backgroundColor: "white",
                                 position: isMobile ? "relative" : "auto",
                                 top: isMobile ? "0" : null,
                                 paddingTop: isMobile ? `${20}px` : '30px'
                              }}>
                              <Box
                                 style={{ whiteSpace: 'pre-line' }}
                              >
                                 <Typography
                                    sx={{
                                       fontSize: isMobile ? 14 : 16,
                                       lineHeight: "25px",
                                       margin: 0,
                                       fontWeight: 400
                                    }}
                                 >
                                    {isMobile && contents.length ? parse(contents[0]) : parse(content)}
                                 </Typography>
                              </Box>
                           </Grid>
                           <Grid item md={6} xs={12} sx={{
                              paddingRight: isMobile ? '20px' : '28px',
                              paddingLeft: isMobile ? '20px' : '15px',
                              paddingTop: isMobile ? 0 : '40px',
                              paddingBottom: isMobile ? "25px" : 0,
                              backgroundColor: "white",
                              position: isMobile ? "relative" : "auto",
                              top: isMobile ? "0" : null
                           }}>
                              <Vimeo
                                 key={`vimeo-mobile-${currentLesson.video?.ID}-${showOrNotNextLessonModal}`}
                                 controls={showOrNotNextLessonModal ? false : true}
                                 color="fd3300"
                                 showTitle={false}
                                 video={currentLesson.video?.ID}
                                 responsive={true}
                                 playsInline={true}
                                 autoplay={false}
                                 onLoaded={(event) => {
                                    setTimeout(() => setVideoLoaded(true), 800);
                                 }}
                                 onProgress={onProgressUpdate}
                                 onEnd={(event: EndEvent) => {
                                    setVideoBanner(false);
                                    endLesson();
                                    let vip =
                                       currentLesson.lesson_type === "cta" ||
                                          currentLesson.cta
                                          ? "Yes"
                                          : "No";
                                    lessonVideoCompleteEvent(
                                       event.seconds,
                                       accelerator,
                                       currentLesson,
                                       vip,
                                       "RSVP VIP Golf School"
                                    );

                                    setShowVideoEnd(true);
                                    setPrevVideoId(currentLesson.video?.ID);
                                 }}
                                 onPause={(event: PauseEvent) => {
                                    handleVideoPause();
                                    if (
                                       event.seconds &&
                                       event.seconds.toFixed(0) !==
                                       event.duration.toFixed(0)
                                    ) {
                                       pausedVideoEvent(
                                          event.seconds,
                                          accelerator,
                                          currentLesson
                                       );
                                    }
                                 }}
                                 onPlay={(event: PlayEvent) => {
                                    handleVideoPlay();
                                    if (event) {
                                       let vip =
                                          currentLesson.lesson_type === "cta" ||
                                             currentLesson.cta
                                             ? "Yes"
                                             : "No";
                                       startedVideoEvent(
                                          event.seconds,
                                          accelerator,
                                          currentLesson,
                                          vip,
                                          "RSVP VIP Golf School"
                                       );
                                       setShowVideoEnd(false);
                                       if (prevVideoId === currentLesson.video.ID) {
                                          setVideoBanner(true);
                                          setShowModal(false);
                                          setShowOrNotNextLessonModal(false);
                                          setShowPopupDialog(false);
                                       }
                                    }
                                 }}
                                 style={{
                                    display: videoLoaded ? "block" : "none",
                                    // width: "100%",
                                    background: "#ffffff",
                                    width: isMobile ? "calc(100% + 20px)" : "100%",
                                    margin: isMobile ? "-10px" : "0",
                                 }}
                              />
                              <Grid item md={12} xs={12} sx={{ paddingTop: '15px' }}>
                                 <Button
                                    onClick={() => {
                                       window.open(currentLesson.video.button_url, "_blank");
                                       clickedVipGolfSchool(accelerator, currentLesson, "RSVP Content Page")
                                    }}
                                    fullWidth
                                    sx={{
                                       zIndex: '0 !important',
                                       borderRadius: "5px",
                                       height: '41px',
                                       margin: isMobile ? 0 : '0 2px',
                                       marginTop: isMobile ? '5px' : 0,
                                    }}
                                    className={styles.ctaButton}
                                 >
                                    <Typography
                                       sx={{
                                          fontSize: 16,
                                          lineHeight: "22px",
                                          margin: 0,
                                          fontWeight: 700,
                                          color: 'white'
                                       }}
                                    >
                                       {parse(currentLesson.video.button_text)}
                                    </Typography>
                                 </Button>
                                 <Box sx={{
                                    paddingTop: '10px',
                                    paddingLeft: isMobile ? 0 : '4px',
                                    paddingRight: isMobile ? 0 : '4px',
                                    borderBottom: '1px solid #D9D9D9',
                                    backgroundColor: "white"
                                 }}>
                                    <Typography
                                       sx={{
                                          fontSize: 14,
                                          lineHeight: "20px",
                                          margin: 0,
                                          fontWeight: 400
                                       }}
                                       id="textUnderButton"
                                    >
                                       {parse(currentLesson.video.text_under_button)}
                                    </Typography>
                                 </Box>
                              </Grid>
                           </Grid>
                           {isMobile && contents.length > 1 && (
                              <Grid
                                 sx={{
                                    overflow: isMobile ? 'auto' : 'hidden',
                                    padding: '0 20px',
                                    paddingLeft: isMobile ? '20px' : '28px',
                                    backgroundColor: "white",
                                    position: isMobile ? "relative" : "auto",
                                    top: isMobile ? "0" : null
                                 }}
                                 id="secondContent">
                                 <Box
                                    style={{ whiteSpace: 'pre-line' }}
                                 >
                                    <Typography
                                       sx={{
                                          fontSize: isMobile ? 14 : 16,
                                          lineHeight: "25px",
                                          margin: 0,
                                          fontWeight: 400
                                       }}
                                    >
                                       {parse(contents[1])}
                                    </Typography>
                                 </Box>
                              </Grid>
                           )}
                        </Grid>
                        {isMobile && nextCourseLesson.ID &&
                           continueBar()
                        }
                     </Grid>
                  )}

                  {!videoLoaded && (
                     <Box
                        sx={{
                           display: "flex",
                           position: "absolute",
                           backgroundColor: "#ffffff",
                           height: "100%",
                           width: "100%",
                           top: 0,
                           left: 0,
                        }}
                     >
                        <Spinner sx={{ margin: "auto" }} />
                     </Box>
                  )}

                  {isShowChampionsPassModal && <></>}
                  {showPopupDialog && (
                     <>
                        {currentPromo?.ID === 124 ? (
                           <></>
                        ) : (isMobile ? (
                           <PopUpDialogMobile
                              showPop={true}
                              accelName={accelerator.name}
                              source={"player"}
                              currentLesson={currentLesson}
                              promo={currentPromo}
                              completedCourse={completedCourse}
                              callback={(showOrNot) => {
                                 setShowPopupDialog(showOrNot);
                              }}
                              accelerator={accelerator}
                           />
                        ) : (
                           <PopUpDialog
                              accelName={accelerator.name}
                              source={"player"}
                              currentLesson={currentLesson}
                              promo={currentPromo}
                              completedCourse={completedCourse}
                              callback={(showOrNot) => {
                                 setShowPopupDialog(showOrNot);
                              }}
                              accelerator={accelerator}
                           />)
                        )}
                     </>
                  )}
               </Box>
               {(((accelerator.url.split("/").filter(Boolean).pop() === "pin-high-approach-shots" && currentLesson.url.split("/").filter(Boolean).pop() === "welcome-to-pin-high-approach-shots") || (accelerator.url.split("/").filter(Boolean).pop() === "green-stopping-power-protocol" && currentLesson.url.split("/").filter(Boolean).pop() === "welcome-to-the-green-stopping-power-protocol")) ||
                  showLessonSurveyBanner)  && (
                  <Box
                     ref={lessonSurveyRef}
                     sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        padding: isMobile ? "8px 8px" : "8px 16px",
                        backgroundColor: "#333333",
                        position: "sticky",
                        zIndex: 2,
                        top: isMobile
                           ? `${playerTitleHeight + playerHeight}px`
                           : 0,
                     }}
                  >
                     <Box
                        sx={{
                           display: "flex",
                           flex: "1",
                           alignItems: "center",
                        }}
                     >
                        <Box
                           component={"img"}
                           sx={{ 
                              marginRight: "6px",
                              width: isMobile ? "16px" : "20px",
                              height: isMobile ? "16px" : "20px",
                            }}
                           src="https://members.performancegolf.com/wp-content/uploads/2026/01/circleWhite.svg"
                           alt="play"
                        />
                        <Typography className={styles.courseFocus}>Set Course Focus</Typography>
                        <Typography className={styles.saveTime}>Save time and get results faster!</Typography>
                     </Box>
                     <Button
                        onClick={() => {
                           if (showLessonSurveyBanner) {
                              setShowLessonSurvey(true);
                              setLocationForMixpanle("Set Course Focus");
                           } else {
                              setShowSwingSurveydDialog(true);
                           }
                        }}
                        className={styles.swingSurveyButton}
                     >
                        <span
                           style={{
                              fontSize: isMobile ? "15px" : "16px",
                              lineHeight: isMobile ? "18px" : "24px",
                              fontWeight: 400,
                           }}
                        >
                           Take Swing Survey
                        </span>
                     </Button>
                  </Box>
               )}
               {(currentLesson.cta || currentLesson.lesson_type === "cta") && (
                  <Button
                     ref={ctaRef}
                     onClick={() => {
                        window.open(currentLesson.cta?.url, "_blank");
                        clickVipProductRecommendation(
                           "Below Video",
                           currentLesson,
                           accelerator
                        );
                        clickedVipButtonGolfSchool(
                           "Below Video Button",
                           currentLesson,
                           accelerator
                        );
                     }}
                     fullWidth
                     sx={{
                        position: isMobile ? "sticky" : "relative",
                        scrollMarginTop: isMobile
                           ? `${playerTitleHeight + playerHeight + lessonSurveyHeight}px`
                           : 0,
                        top: isMobile
                           ? `${playerTitleHeight + playerHeight + lessonSurveyHeight}px`
                           : 0,
                        borderRadius: "unset",
                     }}
                     className={styles.ctaButton}
                  >
                     <span
                        ref={spanVipRef}
                        style={{
                           fontSize: isMobile ? "14px" : "24px",
                           lineHeight: isMobile ? "18px" : "24px",
                           fontWeight: 400,
                        }}
                        dangerouslySetInnerHTML={{
                           __html:
                              currentLesson.lesson_type === "cta" && !accelerator.showVipBannerToAllLessons
                                 ? `Learn More About a VIP Experience`
                                 : accelerator.showVipBannerToAllLessons ? accelerator.ctaTextForAllLessons : currentLesson.cta.text,
                        }}
                     />
                     <span
                        style={{
                           textDecoration: "underline",
                           marginLeft: "8px",
                           fontSize: isMobile ? "14px" : "24px",
                           lineHeight: isMobile ? "18px" : "24px",
                           fontWeight: 400,
                        }}
                     >
                        {" "}
                        Click Here
                     </span>
                  </Button>
               )}
               {(prevVideoLesson || nextVideoLesson) && !isMobile767 && (
                  <Box
                     ref={prevNextRef}
                     sx={{
                        display: "flex", flexWrap: "wrap", alignItems: "stretch",
                        position: isMobile ? "sticky" : "relative",
                        scrollMarginTop: isMobile
                           ? `${playerTitleHeight + playerHeight + contentHeight + ctaButtonHeight + lessonSurveyHeight}px`
                           : 0,
                        top: isMobile
                           ? `${playerTitleHeight + playerHeight + contentHeight + ctaButtonHeight + lessonSurveyHeight}px`
                           : 0,
                        zIndex: "2",
                        backgroundColor: "#fff"
                     }}
                  >  
                     {prevVideoLesson?.url && (
                        <Box
                           sx={{
                              flex: '1 1 50%',
                              '&:only-child': {
                                 flex: '1 1 100%',
                              }
                           }}>
                           <button onClick={() => { playNextLesson(prevVideoLesson, false) }} style={{
                              display: "flex",
                              padding: "18px",
                              border: "1px solid #E8E8E8",
                              background: "transparent",
                              width: "100%",
                              gap: "17px",
                              height: "100%",
                              alignItems: "center",
                              justifyContent: nextVideoLesson ? "flex-start" : "space-between",
                              flexDirection: nextVideoLesson ? "row" : "row-reverse",
                           }}>
                              <Box
                                 sx={{
                                    position: 'relative',
                                    width: '84px',
                                    maxHeight: "50px",
                                    minWidth: "84px"
                                 }}
                              >
                                 <Box
                                    component="img"
                                    src={prevVideoLesson?.lesson_thumbnail_image}
                                    sx={{
                                       width: '100%',
                                       borderRadius: '3px',
                                       display: 'block',
                                       maxHeight: "50px",
                                    }}
                                 />
                                 <Box
                                    sx={{
                                       position: 'absolute',
                                       top: '50%',
                                       left: '50%',
                                       transform: 'translate(-50%, -50%)',
                                       width: '24px',
                                       height: '24px',
                                       display: 'flex',
                                       alignItems: 'center',
                                       justifyContent: 'center',
                                       cursor: 'pointer',
                                    }}
                                 >
                                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                                       <path d="M0 12C0 5.37258 5.37258 0 12 0C18.6274 0 24 5.37258 24 12C24 18.6274 18.6274 24 12 24C5.37258 24 0 18.6274 0 12Z" fill="white" fill-opacity="0.6"/>
                                       <path d="M10.8 12L15.4 16.6L14 18L8 12L14 6L15.4 7.4L10.8 12Z" fill="#FD3300"/>
                                    </svg>
                                 </Box>
                              </Box>
                              <Box sx={{
                                 textAlign: "left"
                              }}>
                                 <Typography sx={{
                                    color: "#E8330A",
                                    fontFamily: "Repro Mono",
                                    fontSize: "14px",
                                    letterSpacing: "1.4px"
                                 }}>
                                    ← PREVIOUS
                                 </Typography>
                                 <Typography sx={{
                                    color: "#121212",
                                    fontFamily: "Repro",
                                    fontSize: "16px",
                                    fontWeight: "700"
                                 }}>
                                    {parse(prevVideoLesson.name)}
                                 </Typography>
                              </Box>
                           </button>
                        </Box>
                     )}
                     {nextVideoLesson?.url && (
                        <Box
                           sx={{
                              flex: '1 1 50%',
                              '&:only-child': {
                                 flex: '1 1 100%',
                              },
                           }}>
                           <button onClick={() => { playNextLesson(nextVideoLesson, false) }} style={{
                              display: "flex",
                              padding: "18px",
                              border: "1px solid #E8E8E8",
                              background: "transparent",
                              justifyContent: "space-between",
                              width: "100%",
                              gap: "17px",
                              height: "100%",
                              alignItems: "center",
                           }}>
                              <Box sx={{
                                 textAlign: "left"
                              }}>
                                 <Typography sx={{
                                    color: "#E8330A",
                                    fontFamily: "Repro Mono",
                                    fontSize: "14px",
                                    letterSpacing: "1.4px"
                                 }}>
                                    {!prevVideoLesson ? "NEXT VIDEO →" : "NEXT →"}
                                 </Typography>
                                 <Typography sx={{
                                    color: "#121212",
                                    fontFamily: "Repro",
                                    fontSize: "16px",
                                    fontWeight: "700"
                                 }}>
                                    {parse(nextVideoLesson.name)}
                                 </Typography>
                              </Box>
                              <Box
                                 sx={{
                                    position: 'relative',
                                    width: '84px',
                                    maxHeight: "50px",
                                    minWidth: "84px"
                                 }}
                              >
                                 <Box
                                    component="img"
                                    src={nextVideoLesson?.lesson_thumbnail_image}
                                    sx={{
                                       width: '100%',
                                       borderRadius: '3px',
                                       display: 'block',
                                       maxHeight: "50px",
                                    }}
                                 />
                                 <Box
                                    sx={{
                                       position: 'absolute',
                                       top: '50%',
                                       left: '50%',
                                       transform: 'translate(-50%, -50%)',
                                       width: '24px',
                                       height: '24px',
                                       display: 'flex',
                                       alignItems: 'center',
                                       justifyContent: 'center',
                                       cursor: 'pointer',
                                    }}
                                 >
                                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                                       <path d="M0 12C0 5.37258 5.37258 0 12 0C18.6274 0 24 5.37258 24 12C24 18.6274 18.6274 24 12 24C5.37258 24 0 18.6274 0 12Z" fill="white" fill-opacity="0.6"/>
                                       <path d="M12.6 12L8 7.4L9.4 6L15.4 12L9.4 18L8 16.6L12.6 12Z" fill="#FD3300"/>
                                    </svg>
                                 </Box>
                              </Box>
                           </button>
                        </Box>
                     )}
                  </Box>
               )}

               {isMobile && currentLesson.lesson_type !== "text" && (
                  <>
                     <Box
                        ref={courseHeaderRef}
                        sx={{
                           padding: "24px",
                           paddingRight: "16px",
                           backgroundColor: "#F0F1F4",
                           position: "sticky",
                           top: playerHeight + playerTitleHeight + contentHeight + prevNextHeight + lessonSurveyHeight + (currentLesson.lesson_type === "cta" || currentLesson.cta ? (ctaRef.current?.offsetHeight ?? 0) : 0),
                           zIndex: 2,
                        }}
                     >
                        <Typography
                           sx={{
                              fontSize: 22,
                              lineHeight: "22px",
                              margin: 0,
                              fontWeight: 500,
                              textTransform: "uppercase",
                              fontFamily: 'Repro',
                           }}
                        >
                           {parse(accelerator.name)}
                           <br />
                           <Typography
                              component="span"
                              sx={{
                                 fontSize: 16,
                                 lineHeight: "16px",
                                 fontWeight: 400,
                                 fontFamily: 'Repro',
                                 textTransform: 'capitalize',
                              }}
                           >
                              {isMobile && (
                                 <>
                                    ({videoLessonCount}{" "}
                                    {videoLessonCount > 1 ? window.pg?.l10n.videos : window.pg?.l10n.video})
                                 </>
                              )}
                           </Typography>
                        </Typography>
                        <Box className={styles.statusCourse}>
                           <Typography sx={{ fontSize: "14px", margin: "5px 0", }}>{acceleratorProgress}% <span style={{ textTransform: 'capitalize' }}>{window.pg?.l10n.complete}</span></Typography>
                           <Box ref={switchAnchorMobileRef}
                              sx={{
                                 display: "inline-flex",
                                 alignItems: "center",
                                 gap: "6px",
                              }}
                           >
                              {/* Info icon */}
                              <Tooltip title="Enable / disable autoplay">
                                 <Box
                                    sx={{
                                       width: 12,
                                       height: 12,
                                       backgroundColor: "#000",
                                       borderRadius: "50%",
                                       color: "#F4F2F0",
                                       display: "flex",
                                       alignItems: "center",
                                       justifyContent: "center",
                                       fontSize: isMobile ? 12 : 13,
                                       fontWeight: 700,
                                       lineHeight: 1,
                                       cursor: "default",
                                       userSelect: "none"
                                    }}
                                 >
                                    i
                                 </Box>
                              </Tooltip>

                              {/* Autoplay text */}
                              <Typography
                                 sx={{
                                    color: "black",
                                    fontFamily: "Repro",
                                    fontSize: "14px",
                                    fontWeight: 400,
                                    lineHeight: "14px",
                                    margin: 0,
                                    marginBottom: "2px",
                                 }}
                              >
                                 {window.pg?.l10n.auto_paly}
                              </Typography>

                              {/* Switch */}
                              <Switch
                                 checked={autoplayNextForCourse}
                                 onChange={handleAutoplayToggle}
                                 sx={{
                                    width: isMobile ? '24px' : '32px',
                                    height: isMobile ? '15px' : '20px',
                                    padding: 0,

                                    '& .MuiSwitch-switchBase': {
                                       padding: '1px',
                                       '&.Mui-checked': {
                                          color: '#FFFFFF',
                                          transform: isMobile ? 'translateX(9px)' : 'translateX(12px)',
                                          '& + .MuiSwitch-track': {
                                             backgroundColor: '#FD3300',
                                             opacity: 1,
                                          },
                                       },
                                    },

                                    '& .MuiSwitch-thumb': {
                                       backgroundColor: '#FFFFFF',
                                       width: isMobile ? '13px' : '18px',
                                       height: isMobile ? '13px' : '18px',
                                    },

                                    '& .MuiSwitch-track': {
                                       backgroundColor: '#B7B7BF',
                                       opacity: 1,
                                       borderRadius: isMobile ? '7.5px' : '10px',
                                    },
                                 }}
                              />
                           </Box>

                           <AutoplayPopover
                              open={open}
                              anchorEl={anchorEl}
                              handleCloseTip={handleCloseTip}
                           />
                        </Box>
                        <BorderLinearProgressHeight6
                           variant="determinate"
                           value={acceleratorProgress}
                        />
                     </Box>

                     <Box className={`${styles.accordion} coursePlaylistLessons`}>{renderPlaylist}</Box>
                     {currentPromo && addToPlaylist && (
                        <div id="upNext">
                           <UpNext
                              promo={currentPromo}
                              //@ts-ignore
                              accelerator={accelerator}
                              lesson={currentLesson}
                           />
                        </div>
                     )}
                  </>
               )}

               {!isMobile && (
                  <Grid sx={{ padding: currentLesson.lesson_type == 'text' && isMobile ? "0 20px" : 0 }}>
                     {
                        <Grid sx={{ marginY: { xs: 3, md: 5 } }}>
                           <Grid container spacing={3}>
                              <Grid item xs={12} md>
                                 <Box
                                    // ref={containerRef}
                                    style={{
                                       width: "100%",
                                       background: "#fff",
                                       paddingTop: "0px",
                                       overflow: "hidden",
                                       marginLeft: 0,
                                    }}
                                    sx={{
                                       height: "auto",
                                       paddingLeft: "0 !important"
                                    }}
                                    className={"mainCoursesContainer"}
                                 >
                                    <div
                                       // ref={containerRef}
                                       key={1}
                                       className="carouselAndTitleAndArrowsContainer"
                                       style={{ width: '100%' }}
                                    >
                                       {popularCoursesOnly?.things?.length > 0 && (
                                          <div className="coursesTrainingCarousel" style={{ fontFamily: "Repro" }} ref={trainingCarouselRef}>
                                             <TrainingCardCarousel
                                                items={popularCoursesOnly.things}
                                                carouselTitle={popularCoursesOnly.name}
                                                width={containerWidth}
                                                parentPage={"course-player"}
                                                isSidebarOpen={false}
                                                locationValue="Footer"
                                             />
                                          </div>
                                       )}
                                    </div>
                                 </Box>
                              </Grid>
                           </Grid>
                        </Grid>
                     }
                  </Grid>
               )}
               
            </Grid>

            {!isMobile && (
               <Grid
                  item
                  md={4}
                  sx={{
                     // maxHeight: playerHeight + playerTitleHeight,
                     overflow: "hidden",
                  }}
               >
                  <Box
                     ref={courseHeaderRef}
                     sx={{
                        padding: "24px 16px 24px 16px",
                        backgroundColor: "#F4F2F0",
                        fontFamily: "Repro"
                     }}
                  >
                     <Typography
                        sx={{
                           fontSize: 22,
                           lineHeight: "22px",
                           margin: 0,
                           fontWeight: 500,
                           textTransform: "uppercase",
                           fontFamily: "Repro"
                        }}
                     >
                        {parse(accelerator.name)}
                        <Typography
                           component="span"
                           sx={{
                              fontSize: 16,
                              lineHeight: "16px",
                              fontWeight: 400,
                              display: "block",
                              marginTop: "10px",
                              fontFamily: "Repro",
                              textTransform: 'capitalize',
                           }}
                        >
                           ({videoLessonCount}{" "}
                           {videoLessonCount > 1 ? window.pg?.l10n.videos : window.pg?.l10n.video})
                        </Typography>
                     </Typography>
                     <Box className={styles.statusCourse}>
                        <Typography sx={{ fontSize: 14, margin: "5px 0" }}>{acceleratorProgress}% <span style={{ textTransform: 'capitalize' }}>{window.pg?.l10n.complete}</span></Typography>
                        <Box sx={{ paddingTop: "2px" }}>
                           <Box
                              sx={{
                                 display: "inline-flex",
                                 alignItems: "center",
                                 gap: "6px",
                              }}
                           >
                              {/* Info icon */}
                              <Tooltip title="Enable / disable autoplay">
                                 <Box
                                    sx={{
                                       width: 12,
                                       height: 12,
                                       backgroundColor: "#000",
                                       borderRadius: "50%",
                                       color: "#F4F2F0",
                                       display: "flex",
                                       alignItems: "center",
                                       justifyContent: "center",
                                       fontSize: 11,
                                       fontWeight: 700,
                                       lineHeight: 1,
                                       cursor: "default",
                                       userSelect: "none"
                                    }}
                                 >
                                    i
                                 </Box>
                              </Tooltip>

                              {/* Autoplay text */}
                              <Typography
                                 sx={{
                                    margin: 0,
                                 }}
                              >
                                 {window.pg?.l10n.auto_paly}
                              </Typography>

                              {/* Switch */}
                              <Box ref={switchAnchorDesktopRef} sx={{display: "flex", height: '15px' }}>
                              <Switch
                                 checked={autoplayNextForCourse}
                                 onChange={handleAutoplayToggle}
                                 sx={{
                                    width: '24px',
                                    height: '16px',
                                    padding: 0,

                                    '& .MuiSwitch-switchBase': {
                                       padding: '2px',
                                       '&.Mui-checked': {
                                          color: '#FFFFFF',
                                          transform: 'translateX(8px)',
                                          '& + .MuiSwitch-track': {
                                             backgroundColor: '#FD3300',
                                             opacity: 1,
                                          },
                                       },
                                    },

                                    '& .MuiSwitch-thumb': {
                                       backgroundColor: '#FFFFFF',
                                       width: '12px',
                                       height: '12px',
                                    },

                                    '& .MuiSwitch-track': {
                                       backgroundColor: '#B7B7BF',
                                       opacity: 1,
                                       borderRadius: '12px',
                                    },
                                 }}
                              />
                              <AutoplayPopover
                                 open={open}
                                 anchorEl={anchorEl}
                                 handleCloseTip={handleCloseTip}
                              />
                              </Box>
                           </Box>
                        </Box>
                     </Box>
                     <BorderLinearProgressHeight6
                        variant="determinate"
                        value={acceleratorProgress}
                     />
                  </Box>

                  <Scrollbar
                     ref={scrollbarsRef}
                     style={{
                        height:
                           playerHeight + playerTitleHeight - courseTitleHeight + carouselHeight + prevNextHeight,
                     }}
                     noScrollX={true}
                  >
                     <Box>{renderPlaylist}</Box>
                     {currentPromo && addToPlaylist && (
                        <UpNext
                           promo={currentPromo}
                           accelerator={accelerator}
                           lesson={currentLesson}
                        />
                     )}
                  </Scrollbar>
               </Grid>
            )}
         </Grid>
      </>);
};

export default CoursePlayer;
